import { ensurePurchaseLineIds, newPurchaseLine } from '../purchaseLines';
import PermissionButton, { currentCan } from '../../../permissions/PermissionButton';
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Modal,
  Card,
  message,
  notification,
  Button,
  Typography,
  Row,
  Col,
  Spin,
  Alert,
  Tag,
  Form,
  Input,
  Space,
  Select,
  Descriptions,
  Drawer
} from "antd";

import {
  UndoOutlined,
  CloudDownloadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import EditableDateField from "../../../utils/EditableDateField";
import { formatAmount } from "../../../utils/formatAmount";
import {
  useFetchCommandeAchatById,
  useFetchItemsInCommandeAchat,
  useValidateCommandeAchat,
  useTransferCommandeToReception,
  useSaveCommandeAchat,
  useListDBFFiles,
  useImportFromDBF,
} from "../../../hooks/achatCommandeHooks";
import AddItemToCommandeForm from './AddItemToCommandeForm';
// Removed payment and delivery modes in achat modules per requirement
import ChangesSummaryModal from '../../../utils/ChangesSummaryModal';
import NestedAchatTable from '../NestedAchatTable';
import PriceUpdateModal from '../PriceUpdateModal';
import FourList from '../../four/FourList';
import DBFImportModal from '../DBFImportModal';
import { motion } from 'framer-motion';
// price updates handled via PriceUpdateModal

const { Title, Text } = Typography;
const { Option } = Select;

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString() : "Not provided";

const EditableField = ({
  value,
  onChange,
  options,
  inputType = "input",
  userPermissions,
  commandeStatus
}) => {
  const canEdit = currentCan('purchaseOrders', 'update') && (userPermissions?.isManager || userPermissions?.isDevisManager || (!commandeStatus?.VALIDER && !commandeStatus?.TRANSFERER));

  const handleChange = (e) => {
    if (!canEdit) return;
    const newValue = inputType === "select" ? e : e.target.value;
    onChange(newValue);
  };

  if (inputType === "select") {
    return (
      <Select
        value={value}
        onChange={handleChange}
        style={{ width: '200px' }}
        disabled={!canEdit}
      >
        {options.map(option => (
          <Option key={option} value={option}>{option}</Option>
        ))}
      </Select>
    );
  }

  return (
    <Input
      value={value}
      onChange={handleChange}
      style={{ width: '200px' }}
      disabled={!canEdit}
    />
  );
};

const SingleCommande = ({
  id,
  onClose,
  userPermissions = {isManager: false, isDevisManager: false},
  onChangesSaved,
  onChangesDetected
}) => {
  const {
    data: initialCommande,
    loading: loadingCommande,
    error: errorCommande,
    refetch,
  } = useFetchCommandeAchatById(id);
  const {
    items: initialItems,
    loading: loadingItems,
    error: errorItems,
    refetch: refetchItems,
  } = useFetchItemsInCommandeAchat(id);
  const { validate, error: validateError, isValidated } = useValidateCommandeAchat();
  const { transfer, error: transferError, isTransferred } = useTransferCommandeToReception();
  const { save, error: saveError } = useSaveCommandeAchat();

  const [isAddItemDrawerVisible, setIsAddItemDrawerVisible] = useState(false);
  const [isDBFImportModalVisible, setIsDBFImportModalVisible] = useState(false);
  const [fournisseurDrawerOpen, setFournisseurDrawerOpen] = useState(false);

  const [localCommande, setLocalCommande] = useState(null);
  const [localItems, setLocalItems] = useState([]);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  const [totals, setTotals] = useState({ totalTTC: 0, totalHT: 0 });
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);

  const prepareCommandeForSave = (commande) => {
    const { fournisseur, ...cleanCommande } = commande;
    return {
      ...cleanCommande,
      CODE_FRS: cleanCommande.CODE_FRS,
      MNT_HT: parseFloat(cleanCommande.MNT_HT || 0),
      MNT_TTC: parseFloat(cleanCommande.MNT_TTC || 0),
      REMISEG: cleanCommande.REMISEG ? parseFloat(cleanCommande.REMISEG) : null,
    };
  };

  useEffect(() => {
    if (localItems) {
      const newTotals = calculateTotals(localItems);
      setTotals(newTotals);
      setLocalCommande(prevCommande => (
        prevCommande
          ? { ...prevCommande, MNT_TTC: newTotals.totalTTC, MNT_HT: newTotals.totalHT }
          : prevCommande
      ));
    }
  }, [localItems]);

  const handleFieldChange = (field, value) => {
    setLocalCommande(prevCommande => ({
      ...prevCommande,
      [field]: value
    }));
    setHasLocalChanges(true);
  };

  const handleFournisseurSelect = (four) => {
    if (!four?.code_frs) return;
    setLocalCommande((prev) => ({
      ...prev,
      CODE_FRS: four.code_frs,
      FOURNISSEUR: four.sociale,
      fournisseur: {
        ...(prev?.fournisseur || {}),
        ...four,
      },
    }));
    setHasLocalChanges(true);
    setFournisseurDrawerOpen(false);
    message.success(`Fournisseur changé: ${four.sociale}${four.compte ? ` (${four.compte})` : ''}`);
  };

  // Initialize local state when data loads (same pattern as SingleReception:
  // independent effects, so items are not gated on the commande header and vice versa)
  useEffect(() => {
    if (initialCommande) {
      setLocalCommande({ ...initialCommande });
    }
  }, [initialCommande]);

  useEffect(() => {
    if (initialItems) {
      setLocalItems(ensurePurchaseLineIds(initialItems));
    }
  }, [initialItems]);

  useEffect(() => {
    console.log("localItems/Commande have been updated:", localItems, localCommande);
  }, [localItems, localCommande]);

  const handleSave = (row) => {
    setLocalItems((prevItems) =>
      prevItems.map((item) =>
        item.id === row.id ? { ...item, ...row } : item
      )
    );
    setHasLocalChanges(true);
  };

  const handleAddItemSuccess = (payload) => {
    const itemsToAdd = Array.isArray(payload) ? payload : [payload];
    setLocalItems((prevItems) => {
      const updated = [...prevItems];
      itemsToAdd.forEach((newItem) => {
        if (!newItem || !newItem.CODE_ART) return;
        updated.push(newPurchaseLine(newItem));
      });
      return updated;
    });
    setHasLocalChanges(true);
    message.success(itemsToAdd.length > 1 ? `${itemsToAdd.length} articles ajoutés` : 'Article ajouté');
  };

  const handleDelete = (lineId) => {
    setLocalItems((prevItems) => prevItems.filter(item => item.id !== lineId));
    setHasLocalChanges(true);
    message.success("Article supprimé localement. Cliquez sur 'Enregistrer' pour sauvegarder.");
  };

  const calculateTotals = (items) => {
    return items.reduce((acc, item) => {
      const quantity = item.QTE || 0;
      const remise = item.REMISE || 0;
      // Use existing TotalHT/TotalTTC if available, otherwise calculate with full precision (no rounding)
      const totalHT = item.TotalHT !== undefined ? item.TotalHT : (quantity * item.PA_HT * (1 - remise / 100));
      const totalTTC = item.TotalTTC !== undefined ? item.TotalTTC : (quantity * item.PA_TTC * (1 - remise / 100));
      return {
        totalTTC: acc.totalTTC + totalTTC,
        totalHT: acc.totalHT + totalHT
      };
    }, { totalTTC: 0, totalHT: 0 });
  };

  const handleSaveChanges = () => {
    setIsSummaryModalVisible(true);
  };

  useEffect(() => {
    if (hasLocalChanges && onChangesDetected) {
      onChangesDetected();
    }
  }, [hasLocalChanges, onChangesDetected]);

  const confirmAndSaveChanges = async () => {
    try {
      setIsSaving(true);
      const dataToSave = await new Promise((resolve) => {
        setLocalCommande((currentCommande) => {
          setLocalItems((currentItems) => {
            const cleanItems = currentItems.map(({ article, ...item }) => {
              const quantity = item.QTE || 0;
              const remise = item.REMISE || 0;
              // Calculate totals without rounding - only final document totals should be rounded
              const totalHT = item.TotalHT !== undefined ? item.TotalHT : (quantity * item.PA_HT * (1 - remise / 100));
              const totalTTC = item.TotalTTC !== undefined ? item.TotalTTC : (quantity * item.PA_TTC * (1 - remise / 100));
              return {
                ...item,
                TotalHT: totalHT,
                TotalTTC: totalTTC,
              };
            });

            const data = {
              commande: prepareCommandeForSave(currentCommande),
              items: cleanItems
            };
            resolve(data);
            return currentItems;
          });
          return currentCommande;
        });
      });

      console.log("Data to Save:", dataToSave);
      await save(dataToSave.commande, dataToSave.items);
      setHasLocalChanges(false);
      if (onChangesSaved) {
        onChangesSaved();
      }
      setIsSummaryModalVisible(false);
      setIsSaving(false);
      message.success("Commande enregistrée avec succès!");
      refetch();
      refetchItems();
    } catch (error) {
      console.error("Error saving commande:", error);
      message.error(`Échec de l'enregistrement de la commande: ${error.message || error}`);
      setIsSaving(false);
    }
  };

  const handleValidateClick = async () => {
    try {
      await validate(id);
      message.success('La commande a été validée avec succès.');
      refetch();
    } catch (error) {
      message.error(`Échec de la validation de la commande: ${error}`);
    }
  };

  const handleTransferClick = async () => {
    try {
      const result = await transfer(localCommande.REF_CA);
      notification.success({
        message:'La commande a été transférée avec succès.',
        description: `Bon de réception créé: ${result?.REF_BR_A || ''}`,
        placement: 'bottomLeft',
      });

      refetch();
      onClose();
    } catch (error) {
      message.error(`Échec du transfert de la commande: ${error}`);
    }
  };

  const openPriceModal = () => setIsPriceModalOpen(true);
  const closePriceModal = () => setIsPriceModalOpen(false);

  const handleDBFImport = async (records) => {
    if (!records || records.length === 0) {
      message.warning('Aucun enregistrement à importer');
      return;
    }

    try {
      // Each imported row remains an independent purchase line.
      const addedItems = records.filter(record => record?.CODE_ART).map(record => newPurchaseLine({
        ...record,
        REF_CA: localCommande.REF_CA,
      }));
      const updatedItems = [...addedItems, ...localItems];
      const addedCount = addedItems.length;

      // Calculate totals for the updated items
      const newTotals = calculateTotals(updatedItems);
      const updatedCommande = {
        ...localCommande,
        MNT_TTC: newTotals.totalTTC,
        MNT_HT: newTotals.totalHT
      };

      // Clean items for saving (remove article relation if present)
      const cleanItems = updatedItems.map(({ article, ...item }) => {
        const quantity = item.QTE || 0;
        const remise = item.REMISE || 0;
        // Calculate totals without rounding - only final document totals should be rounded
        const totalHT = item.TotalHT !== undefined ? item.TotalHT : (quantity * item.PA_HT * (1 - remise / 100));
        const totalTTC = item.TotalTTC !== undefined ? item.TotalTTC : (quantity * item.PA_TTC * (1 - remise / 100));
        return {
          ...item,
          TotalHT: totalHT,
          TotalTTC: totalTTC,
        };
      });

      // Save to backend
      await save(prepareCommandeForSave(updatedCommande), cleanItems);
      
      // Update local state
      setLocalItems(updatedItems);
      setLocalCommande(updatedCommande);
      setHasLocalChanges(false);
      
      // Refetch data from backend
      await refetch();
      await refetchItems();
      
      if (onChangesSaved) {
        onChangesSaved();
      }
      
      message.success(`${addedCount} ligne(s) ajoutée(s)`);
    } catch (error) {
      console.error("Error importing and saving:", error);
      message.error(`Échec de l'importation: ${error.message || error}`);
    }
  };

  const handleClose = () => {
    onClose();
  };


  const getDescriptionItems = () => [
    {
      key: 'fournisseurInfo',
      label: 'Fournisseur',
      children: (
        <div>
          <Row gutter={16}>
            <Col span={24}>
              <Text strong>Fournisseur:</Text> {localCommande.FOURNISSEUR}
              {localCommande.fournisseur?.compte && (
                <span style={{ marginLeft: 8, color: '#666' }}>
                  (Compte: {localCommande.fournisseur.compte})
                </span>
              )}
              {!localCommande.VALIDER && (
                <PermissionButton permissionModule="purchaseOrders" permissionAction="update"
                  type="link"
                  icon={<EditOutlined />}
                  size="small"
                  style={{ padding: '0 4px', marginLeft: 8 }}
                  onClick={() => setFournisseurDrawerOpen(true)}
                />
              )}
            </Col>
          </Row>
        </div>
      ),
      span: 2,
    },
    {
      key: 'globalDiscount',
      label: 'Remise G',
      children: `${localCommande.REMISEG || "Aucune"}%`,
      span: 1,
    },
    {
      key: 'amountTTC',
      label: 'Total(ttc)',
      children: ` ${formatAmount(localCommande.MNT_TTC)}`,
      span: 1,
    },
    {
      key: 'amountHT',
      label: 'Total(ht)',
      children: ` ${formatAmount(localCommande.MNT_HT)}`,
      span: 1,
    },
    {
      key: 'creationDate',
      label: 'Date Commande',
      children: (
        <EditableDateField
          value={localCommande.DATE_CMD}
          onChange={(newDate) => handleFieldChange("DATE_CMD", newDate)}
          disabled={!(userPermissions.isManager || userPermissions.isDevisManager || (!localCommande.VALIDER && !localCommande.TRANSFERER))}
        />
      ),
      span: 1,
    },
    {
      key: 'remarks',
      label: 'Remarque',
      children: (
        <Input.TextArea
          value={localCommande.REMARQUE}
          onChange={(e) => handleFieldChange("REMARQUE", e.target.value)}
          disabled={!(userPermissions.isManager || userPermissions.isDevisManager || (!localCommande.VALIDER && !localCommande.TRANSFERER))}
          autoSize={{ minRows: 2, maxRows: 6 }}
        />
      ),
      span: 2,
    },
  ];

  if (loadingCommande || loadingItems) return <Spin tip="Chargement..." style={{ display: 'block', margin: '50px auto' }} />;
  if (errorCommande || errorItems)
    return (
      <Alert
        message="Error"
        description={errorCommande || errorItems}
        type="error"
        showIcon
      />
    );

  if (!localCommande || !localItems) return <Spin tip="Chargement..." style={{ display: 'block', margin: '50px auto' }} />;

  // Animation variants
  const containerVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    exit: {
      opacity: 0,
      scale: 0.97,
      transition: {
        duration: 0.12,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate={isClosing ? "exit" : "visible"}
      variants={containerVariants}
      style={{
        transformOrigin: 'center center',
        willChange: 'transform, opacity'
      }}
    >
      <Card
        title={
          <>
            Commande d'Achat - {localCommande.REF_CA}
            {localCommande.TRANSFERER ? (
              <Tag color="blue" style={{ marginLeft: "8px" }}>
                Transféré
              </Tag>
            ) : localCommande.VALIDER ? (
              <Tag color="green" style={{ marginLeft: "8px" }}>
                Validé
              </Tag>
            ) : (
              <Tag color="orange" style={{ marginLeft: "8px" }}>
                Enregistré
              </Tag>
            )}
          </>
        }
        bordered={false}
        extra={
          <Space>
            {hasLocalChanges && (
              <PermissionButton
                icon={<UndoOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Annuler les modifications',
                    content: 'Êtes-vous sûr de vouloir annuler toutes les modifications? Cela rétablira toutes les modifications à l\'état sauvegardé précédemment.',
                    okText: 'Oui, Annuler tout',
                    cancelText: 'Non, Garder les modifications',
                    onOk: () => {
                      setLocalCommande({ ...initialCommande });
                      setLocalItems(ensurePurchaseLineIds(initialItems));
                      setHasLocalChanges(false);
                      message.success('Toutes les modifications ont été annulées');
                    }
                  });
                }}
              >
                Annuler modif.
              </PermissionButton>
            )}
            {!localCommande.VALIDER && !localCommande.TRANSFERER && (
              <PermissionButton permissionModule="purchaseOrders" permissionAction="validate" type="primary" onClick={handleValidateClick} disabled={hasLocalChanges}>
                Valider CA
              </PermissionButton>
            )}
            {(userPermissions.isManager || userPermissions.isDevisManager || (localCommande.VALIDER && !localCommande.TRANSFERER)) && (
              <PermissionButton permissionModule="purchaseOrders" permissionAction="transfer" type="primary" onClick={handleTransferClick} disabled={hasLocalChanges}>
                Transférer {'>'} Module BR
              </PermissionButton>
            )}
            <PermissionButton permissionModule="purchaseOrders" permissionAction="update"
              type="primary"
              onClick={handleSaveChanges}
              disabled={!hasLocalChanges}
            >
              Enregistrer Modif.
            </PermissionButton>
            <Button
              type="default"
              onClick={handleClose}
              style={{
                backgroundColor: 'white',
                color: '#EE2625',
                border: '1px solid #EE2625',
              }}
            >
              SORTIR
            </Button>
          </Space>
        }
        style={{ position: 'relative' }}
      >
        <Descriptions
          bordered
          column={{
            xs: 1,
            sm: 2,
            md: 3,
          }}
          size="small"
          items={getDescriptionItems()}
          labelStyle={{ fontWeight: 'bold', textAlign: 'right' }}
          contentStyle={{ minWidth: '150px' }}
        />
      </Card>

      <Card title={`Articles dans la commande (${localItems?.length || 0})`} style={{ marginTop: 20 }} extra={
        <Space>
          <Button onClick={openPriceModal} type="default">Mettre à jour les prix</Button>
          <Button
            icon={<CloudDownloadOutlined />}
            onClick={() => setIsDBFImportModalVisible(true)}
            type="default"
            disabled={!(userPermissions.isManager || userPermissions.isDevisManager) && (localCommande.VALIDER || localCommande.TRANSFERER)}
          >
            Importer FTP
          </Button>
          <PermissionButton permissionModule="purchaseOrders" permissionAction="update"
            onClick={() => setIsAddItemDrawerVisible(true)}
            type="primary"
            disabled={!(userPermissions.isManager || userPermissions.isDevisManager) && (localCommande.VALIDER || localCommande.TRANSFERER)}
          >
            Ajouter Article
          </PermissionButton>
        </Space>
      }>
        <NestedAchatTable
          items={localItems}
          handleSave={handleSave}
          handleDelete={handleDelete}
          validated={localCommande.VALIDER || localCommande.TRANSFERER}
          userPermissions={userPermissions}
          type="commande"
        />
      </Card>

      <Drawer
        title="Ajouter un article à la commande"
        width={900}
        onClose={() => setIsAddItemDrawerVisible(false)}
        open={isAddItemDrawerVisible}
      >
        <AddItemToCommandeForm
          refCommande={localCommande.REF_CA}
          onSuccess={(newItem) => {
            handleAddItemSuccess(newItem);
            setIsAddItemDrawerVisible(false);
          }}
        />
      </Drawer>

      <Drawer
        title="Changer le fournisseur"
        width={900}
        onClose={() => setFournisseurDrawerOpen(false)}
        open={fournisseurDrawerOpen}
      >
        <FourList onSelectFour={handleFournisseurSelect} />
      </Drawer>

      <ChangesSummaryModal
        lineIdentity
        visible={isSummaryModalVisible}
        onClose={() => setIsSummaryModalVisible(false)}
        onConfirm={confirmAndSaveChanges}
        originalBonliv={initialCommande}
        modifiedBonliv={localCommande}
        originalItems={initialItems}
        modifiedItems={localItems}
        loading={isSaving}
      />

      <PriceUpdateModal
        open={isPriceModalOpen}
        onClose={closePriceModal}
        items={localItems}
        onApplied={() => setIsPriceModalOpen(false)}
        title="Mise à jour des prix (Commande)"
      />

      <DBFImportModal
        visible={isDBFImportModalVisible}
        onClose={() => setIsDBFImportModalVisible(false)}
        onImport={handleDBFImport}
        useListHook={useListDBFFiles}
        useImportHook={useImportFromDBF}
      />
    </motion.div>
  );
};

export default SingleCommande;
